import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import FilterPanel from "@/components/feature/FilterPanel";
import SortBar from "@/components/feature/SortBar";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import { applyFilters, defaultFilters, type FilterState } from "@/utils/filter";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryName } = useParams();
  const category = categories.find((c) => c.key === categoryName);
  const [filters, setFilters] = useState<FilterState>({ ...defaultFilters });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const categoryVideos = useMemo(
    () => (category ? videos.filter((v) => v.category === category.key) : []),
    [category]
  );

  const filtered = useMemo(
    () => applyFilters(categoryVideos, filters),
    [categoryVideos, filters]
  );

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-900 text-foreground-500">
          <i className="ri-emotion-sad-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-50">该分类不存在</h1>
        <p className="mt-2 text-sm text-foreground-400">请从下方分类中重新选择你想浏览的内容方向。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={`/category/${c.key}`}
              className="rounded-md border border-background-700 px-4 py-2 text-sm text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-300"
            >
              {c.name}视频
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setVisible(PAGE_SIZE);
  };

  const reset = () => updateFilters({ ...defaultFilters });

  const shown = filtered.slice(0, visible);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span>分类</span>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-200">{category.name}</span>
      </nav>

      <header className="mt-6 flex flex-col gap-4 rounded-lg border border-background-800 bg-background-900 p-5 md:flex-row md:items-center md:justify-between md:p-6">
        <div className="flex items-center gap-4">
          <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
            <i className={`${category.icon} text-2xl`} />
          </span>
          <div>
            <h1 className="font-heading text-xl font-extrabold text-foreground-50 md:text-2xl">
              {category.name}视频合集
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-foreground-400">{category.blurb}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {category.subs.map((sub) => (
            <span
              key={sub}
              className="rounded-full bg-secondary-500/10 px-2.5 py-1 text-xs font-medium text-secondary-300"
            >
              {sub}
            </span>
          ))}
        </div>
      </header>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 rounded-lg border border-background-800 bg-background-900 p-5">
            <FilterPanel subs={category.subs} filters={filters} onChange={updateFilters} onReset={reset} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center gap-3 lg:hidden">
            <button
              type="button"
              onClick={() => setMobileFilterOpen(true)}
              className="flex flex-1 items-center justify-center gap-2 rounded-md border border-background-700 bg-background-900 px-4 py-2.5 text-sm font-semibold text-foreground-100"
            >
              <i className="ri-filter-3-line" /> 筛选
            </button>
          </div>

          <SortBar sort={filters.sort} onSortChange={(sort) => updateFilters({ ...filters, sort })} total={filtered.length} />

          {shown.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    cover={video.cover}
                    streamerName={video.streamerName}
                    streamerAvatar={video.streamerAvatar}
                    duration={video.duration}
                    views={video.views}
                    subcategory={video.subcategory}
                  />
                ))}
              </div>

              {visible < filtered.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 bg-background-900 px-6 py-3 font-label text-sm font-semibold text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-300"
                  >
                    <i className="ri-add-line text-lg" /> 加载更多视频
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-background-800 bg-background-900 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-500">
                <i className="ri-file-search-line text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-50">
                抱歉，没有找到符合条件的视频
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-400">
                试试减少筛选条件，或重置后重新浏览该分类下的全部视频。
              </p>
              <button
                type="button"
                onClick={reset}
                className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-refresh-line text-lg" /> 重置筛选
              </button>
            </div>
          )}
        </div>
      </div>

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-background-950/80"
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-background-700 bg-background-900 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-50">筛选视频</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setMobileFilterOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-700 text-foreground-200"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel subs={category.subs} filters={filters} onChange={updateFilters} onReset={reset} />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 py-3 font-label text-sm font-bold text-background-950"
            >
              查看 {filtered.length} 个结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}